import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useError } from '@/hooks/useError';
import { navigate } from '@/shared/routes';
import type { Article } from '@/shared/articles';
import styles from './Blog.module.scss';

const INITIAL_COUNT = 3;
const LOAD_BATCH = 3;
const ITEM_THRESHOLD = 0.15;

const formatDate = (iso: string, locale: string) => {
  const date = new Date(iso);
  const month = date.toLocaleString(locale, { month: 'short' });
  return `${month} ${date.getFullYear()}`;
};

const Blog = () => {
  const { t, i18n } = useTranslation();
  const { pushError } = useError();

  const listRef = useRef<HTMLUListElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);
  const [articles, setArticles] = useState<Article[]>([]);

  useEffect(() => {
    supabase
      .from('articles')
      .select()
      .then(({ data, error }) => {
        if (error) {
          pushError(t('error.articlesFetchFailed'));
          return;
        }
        if (data) setArticles(data);
      });
  }, [pushError, t]);

  const visibleArticles = articles.slice(0, visibleCount);
  const remaining = articles.length - visibleCount;
  const nextBatch = Math.min(LOAD_BATCH, remaining);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle(styles.visible, entry.isIntersecting);
        });
      },
      { threshold: ITEM_THRESHOLD, rootMargin: '0px 0px -40px 0px' }
    );
    return () => observerRef.current?.disconnect();
  }, []);

  useEffect(() => {
    const list = listRef.current;
    const observer = observerRef.current;
    if (!list || !observer) return;
    Array.from(list.children).forEach((item) => observer.observe(item));
  }, [visibleCount, articles]);

  const isSk = i18n.language === 'sk';

  return (
    <div className={styles.blog}>
      <ul className={styles.list} ref={listRef}>
        {visibleArticles.map((article) => (
          <li key={article.slug} className={styles.item}>
            <button type='button' className={styles.row} onClick={() => navigate(`/article/${article.slug}`)}>
              <div className={styles.metaBar}>
                <div className={styles.metaLeft}>
                  <span className={styles.category}>
                    {t(`blog.categories.${article.category}`, { defaultValue: article.category })}
                  </span>
                  <span className={styles.date}>{formatDate(article.created_at, i18n.language)}</span>
                  <span className={styles.readTime}>
                    <svg width='13' height='13' aria-hidden='true'>
                      <use href='/icons.svg#clock' />
                    </svg>
                    {article.read_min} {t('blog.minRead')}
                  </span>
                  {/* TODO likes */}
                  {/* {article.likes > 0 && ( */}
                  {/*   <span className={styles.likes}> */}
                  {/*     <svg width='13' height='13' aria-hidden='true'> */}
                  {/*       <use href='/icons.svg#heart' /> */}
                  {/*     </svg> */}
                  {/*     {article.likes} */}
                  {/*   </span> */}
                  {/* )} */}
                </div>
                <span className={styles.readLink} aria-hidden='true'>
                  <span>{t('blog.read')}</span>
                  <svg width='14' height='14' aria-hidden='true'>
                    <use href='/icons.svg#arrow-up-right' />
                  </svg>
                </span>
              </div>
              <h3 className={styles.title}>{isSk ? article.title_sk : article.title_en}</h3>
              <p className={styles.excerpt}>{isSk ? article.excerpt_sk : article.excerpt_en}</p>
            </button>
          </li>
        ))}
      </ul>
      {remaining > 0 && (
        <div className={styles.loadMoreWrapper}>
          <button
            type='button'
            className={styles.loadMoreBtn}
            onClick={() => setVisibleCount((prev) => prev + LOAD_BATCH)}
          >
            {t('blog.loadMore', { count: nextBatch })}
          </button>
        </div>
      )}
    </div>
  );
};

export default Blog;
